import { ErrorLogComponent } from '@cian/error-log-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { CardSectionNew } from 'shared/components/CardSectionNew';
import { SimilarOffersAnchor } from 'shared/components/SimilarOffersSection';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import {
  EInfrastructureProfile,
  InfrastructureMicrofrontendContainer,
} from 'shared/containers/InfrastructureMicrofrontendContainer';
import { HomeownerTrapContainer } from 'shared/containers/valuationTraps';
import { BannerTrapContainerLoader } from 'shared/containers/valuationTraps/CPDTrapContainer';

import { FeedbackPopupContainer as FeedBackPopupC } from '../../FeedbackExperiment/components/PopupWithComplaint/FeedbackPopup';
import { PopupFeedbackSentContainer } from '../../FeedbackExperiment/components/PopupWithForm/PopupFeedbackSent';
import { LabelsLayoutNew } from '../../components/LabelsLayout';
import {
  OfferCardPageLayout,
  OfferCardPageLayoutAside,
  OfferCardPageLayoutCenter,
  OfferCardPageLayoutFooter,
  OfferCardPageLayoutTop,
} from '../../components/OfferCardPageLayout';
import { OfferHistoryContainer } from '../../components/OfferHistory/containers';
import { AdminButtonsContainer } from '../../components/admin_buttons/container';
import { HeaderAdminPanelContainer } from '../../components/header_admin_panel/container';
import { OfferBreadcrumbsContainer } from '../../components/offer_breadcrumbs/container';
import { OfferGallery } from '../../components/offer_gallery';
import { OfferIsHiddenBlock } from '../../components/offer_hidden_block';
import { OfferRefusedContainer } from '../../components/offer_refused/container';
import { OfferUnpublishedContainer } from '../../components/offer_unpublished/container';
import { HideOfferTopPopup } from '../../components/popups/hide_offer_top_popup';
import { LegendModalContainer } from '../../components/popups/legend/container';
import { PrintModalContainer } from '../../components/popups/print/container';
import { RemoveModalContainer } from '../../components/popups/remove/container';
import { PrintInformationContainer } from '../../components/print_information/container';
import { PrintPhotoContainer } from '../../components/print_photo/container';
import { isVillagePromotionsShown as isVillagePromotionsShownSelector } from '../../selectors/isVillagePromotionsShown';
import { getOffer } from '../../selectors/newObject';
import { IState } from '../../store';
import { TgbBottomBannersContainer } from '../Adfox/TgbBottomBannersContainer';
import { AsideRedesignedContainer } from '../AsideRedesigned/AsideRedesigned';
import { AuctionLabelContainer } from '../AuctionLabel';
import { BottomAuthorBlockContainer } from '../BottomAuthorBlockNew';
import { CallUnderDescriptionContainer } from '../CallUnderDescriptionContainer';
import { ChatPopupContainer } from '../ChatPopupContainer';
import { ComparisonNotificationLoadableContainer } from '../ComparisonNotificationContainer';
import { DealSaleLabelNewContainer } from '../DealSaleLabelContainer';
import { DepositPropertyLabelContainer } from '../DepositPropertyLabel';
import { DescriptionContainer } from '../Description/Description';
import { FavoritesFoldersContainer } from '../FavoritesFoldersContainer';
import { FlatViewOrderTooltipContainer } from '../FlatViewOrderTooltip';
import { IdenticalOffersContainer } from '../IdenticalOffersContainer';
import { KpInfoContainer } from '../KpInfoContainer';
import { LocationSubscriptionContainer } from '../LocationSubscriptionContainer';
import { MainNewContainer } from '../MainContainer';
import { MortgageBrokerBannerContainer } from '../MortgageBrokerBannerContainer';
import { NavigationContainerNew } from '../NavigationNew';
import { NewbuildingsFromDeveloperLazy } from '../NewbuildingsFromDeveloper';
import { NotAvailablePopupContainer } from '../NotAvailablePopupContainer';
import { ObjectFactoidsContainer } from '../ObjectFactoid/ObjectFactoid';
import { ObjectSummaryTitleContainer } from '../ObjectSummaryTitle/ObjectSummaryTitleContainer';
import { OfferMapContainer } from '../OfferMapContainer';
import { OfferMetaDataContainer } from '../OfferMetaData/OfferMetaData';
import { OfferSummaryInfoNewV2 } from '../OfferSummaryInfoNew';
import { OfferValuationContainer } from '../OfferValuation';
import { GoodPriceLabelNewContainer } from '../OfferValuation/containers/GoodPriceLabel';
import { OnlyCianLabelNewContainer } from '../OnlyCianLabelContainer';
import { PhoneWarningModalContainer } from '../PhoneWarningModalContainer';
import { RecommendedVillagesContainer } from '../RecommendedVillagesContainer';
import { RosReestrLabelContainer } from '../RosReestrLabelContainer';
import { RosreestrInfoContainer } from '../RosreestrInfoContainer';
import { SeoLinksContainer } from '../SeoLinks/SeoLinksContainer';
import { SimilarOffersGroupsContainer } from '../SimilarOffersRedesigned';
import { SuburbanBannerContainer } from '../SuburbanBannerContainer';
import { SuburbanBuildersProjectsBannerContainer } from '../SuburbanBuildersProjectsBannerContainer';
import { SuburbanBuildersProjectsContainer } from '../SuburbanBuildersProjectsContainer';
import { SuburbanCalculatorContainer } from '../SuburbanCalculatorContainer';
import { TourContainer } from '../TourContainer';
import { UxfbCookieUpdater } from '../UxfbCookieUpdater';
import { VillagePromotionsContainer } from '../VillagePromotionsContainer';
import { VisitedOffersUpdater } from '../VisitedOffersUpdater';
import { WarningsContainer } from '../WarningsContainer';

const SuburbanPageContainer = () => {
  const context = useApplicationContext();
  const hiddenObjectsLkUrl = context.config.get<string>('hiddenObjects.lkUrl') || '';

  const offer = useSelector(getOffer);
  const isPublished = offer.status === 'published';
  const isVillagePromotionsShown = useSelector(isVillagePromotionsShownSelector);

  const isHiddenByUser = useSelector((state: IState) => state.offerData.isHiddenByUser);
  const isTopPopupOpened = useSelector((state: IState) => state.hideOffer.isTopPopupOpened);
  const hideOfferStatus = useSelector((state: IState) => state.hideOffer.status);
  const hideOfferErrorMessage = useSelector((state: IState) => state.hideOffer.errorMessage);

  return (
    <div data-testid="SuburbanPage">
      <NavigationContainerNew />
      <OfferCardPageLayout>
        <FeedBackPopupC />

        <PopupFeedbackSentContainer />
        <FlatViewOrderTooltipContainer />
        <OfferCardPageLayoutTop>
          <OfferBreadcrumbsContainer />
          <OfferMetaDataContainer />
          <HeaderAdminPanelContainer />
          <OfferRefusedContainer config={context.config} />
          <OfferUnpublishedContainer />
          <AdminButtonsContainer />
          <ErrorLogComponent logger={context.logger}>
            <WarningsContainer />
          </ErrorLogComponent>
          {!isPublished && <SimilarOffersGroupsContainer />}
        </OfferCardPageLayoutTop>

        {isTopPopupOpened && (
          <HideOfferTopPopup
            status={hideOfferStatus}
            hidden={Boolean(isHiddenByUser)}
            lkUrl={hiddenObjectsLkUrl}
            hideOfferErrorMessage={hideOfferErrorMessage}
          />
        )}

        {isHiddenByUser && <OfferIsHiddenBlock lkUrl={hiddenObjectsLkUrl} />}

        <OfferCardPageLayoutCenter>
          <ErrorLogComponent logger={context.logger}>
            <HomeownerTrapContainer />
            <LabelsLayoutNew>
              <GoodPriceLabelNewContainer />
              <OnlyCianLabelNewContainer />
              <RosReestrLabelContainer />
              <DealSaleLabelNewContainer />
              <AuctionLabelContainer />
              <DepositPropertyLabelContainer />
            </LabelsLayoutNew>
            <MainNewContainer />
          </ErrorLogComponent>
          {isPublished && (
            <div>
              <OfferGallery reactErrorLogger={context.logger} />
              <PrintPhotoContainer />
            </div>
          )}
          <ObjectSummaryTitleContainer />
          <ObjectFactoidsContainer />
          <DescriptionContainer />
          <CallUnderDescriptionContainer />
          <OfferSummaryInfoNewV2 />
          <RosreestrInfoContainer />
          <SuburbanBuildersProjectsContainer />
          <TourContainer />
          {isPublished && [
            <div key="published-data">
              <KpInfoContainer />
              {isVillagePromotionsShown && <VillagePromotionsContainer />}
              <ErrorLogComponent logger={context.logger}>
                <CardSectionNew>
                  <OfferMapContainer />
                </CardSectionNew>
              </ErrorLogComponent>
              <ErrorLogComponent logger={context.logger}>
                <InfrastructureMicrofrontendContainer profile={EInfrastructureProfile.Suburban} />
              </ErrorLogComponent>

              <LocationSubscriptionContainer />

              <ErrorLogComponent logger={context.logger}>
                <RecommendedVillagesContainer />
              </ErrorLogComponent>
              <SuburbanCalculatorContainer />
              <MortgageBrokerBannerContainer />
              <OfferValuationContainer />
              <BannerTrapContainerLoader />
              <OfferHistoryContainer />
              <SuburbanBuildersProjectsBannerContainer />
              <ErrorLogComponent logger={context.logger}>
                <CardSectionNew hideForPrint>
                  <BottomAuthorBlockContainer />
                </CardSectionNew>
              </ErrorLogComponent>
              <CardSectionNew hideForPrint>
                <SuburbanBannerContainer />
              </CardSectionNew>
            </div>,
          ]}
        </OfferCardPageLayoutCenter>

        <OfferCardPageLayoutAside>
          <AsideRedesignedContainer />
        </OfferCardPageLayoutAside>
      </OfferCardPageLayout>
      <OfferCardPageLayoutFooter>
        <NewbuildingsFromDeveloperLazy />
        <IdenticalOffersContainer />
        {isPublished && (
          <SimilarOffersAnchor>
            <SimilarOffersGroupsContainer />
          </SimilarOffersAnchor>
        )}
        <SeoLinksContainer />
        <TgbBottomBannersContainer />
      </OfferCardPageLayoutFooter>

      <PrintModalContainer />
      <PrintInformationContainer />
      <LegendModalContainer />
      <RemoveModalContainer />
      <ChatPopupContainer />
      <NotAvailablePopupContainer />
      <FavoritesFoldersContainer />
      <PhoneWarningModalContainer />
      <ComparisonNotificationLoadableContainer />
      <VisitedOffersUpdater />
      <UxfbCookieUpdater />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default SuburbanPageContainer;
